import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import mediaQueryStore from "./mediaQueryStore";

const useMediaV2 = <T>(queries: string[], values: T[], defaultValue: T): T => {
  const queriesRef = useRef(queries);
  const valuesRef = useRef(values);
  const defaultValueRef = useRef(defaultValue);

  queriesRef.current = queries;
  valuesRef.current = values;
  defaultValueRef.current = defaultValue;

  const queriesHash = useMemo(() => JSON.stringify(queries), [queries]);

  const getValue = useCallback(() => {
    const index = queriesRef.current.findIndex((query) =>
      mediaQueryStore.isMatch(query)
    );

    return typeof valuesRef.current?.[index] !== "undefined"
      ? valuesRef.current[index]
      : defaultValueRef.current;
  }, []);

  const [value, setValue] = useState(() => {
    mediaQueryStore.registerMediaQueries(queriesRef.current);

    return getValue();
  });

  const updateValue = useCallback(() => {
    setValue(getValue());
  }, [getValue]);

  useEffect(() => {
    if (!queriesRef.current?.length) {
      return;
    }

    mediaQueryStore.registerMediaQueries(queriesRef.current);

    queriesRef.current.forEach((query) => {
      mediaQueryStore.subscribe(query, updateValue);
    });

    updateValue();

    return () => {
      queriesRef.current.forEach((query) => {
        mediaQueryStore.unsubscribe(query, updateValue);
      });
    };
  }, [queriesHash, updateValue]);

  return value;
};

export default useMediaV2;
